'use client';

import { useCallback, useEffect, useState } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { getEscalationDetails } from 'src/services/jobs';
import { convertUtcToRelevantTimezone, handleErrorMessages } from 'src/helpers/utils';

interface EscalationData {
  address?: string;
  reason?: string;
  notes?: string;
  flaggedBy?: string;
  flaggedAt?: string;
  deEscalatedBy?: string;
  deEscalatedAt?: string;
  escalatedAt?: string;
  escalationDuration?: string;
}

const EscalationStatusModal = () => {
  const dispatch = useAppDispatch();
  const { extraProps } = useAppSelector((state: Record<string, unknown>) => {
    const modals = state?.modals as Record<string, unknown>;
    return modals?.escalationStatusModal as Record<string, unknown>;
  });

  const modalProps = extraProps as { escalationId?: string; type?: 'escalation' | 'de-escalation'; timezone?: string } | undefined;
  const isDeEscalation = modalProps?.type === 'de-escalation';

  const [data, setData] = useState<EscalationData>({});
  const [isLoading, setIsLoading] = useState(!!modalProps?.escalationId);

  useEffect(() => {
    if (!modalProps?.escalationId) {
        dispatch(updateVisibility({ modalType: 'escalationStatusModal', isVisible: false }));
      return;
    }
    setIsLoading(true);
    getEscalationDetails(modalProps.escalationId, modalProps.type || 'escalation')
      .then((res) => {
        const responseData = res?.data;
        const timezone = modalProps?.timezone;
        const formatDate = (iso: string, format = 'MMM D, YYYY [at] h:mm A z') => {
          if (timezone) {
            const result = convertUtcToRelevantTimezone<string>(iso, timezone, format);
            if (typeof result === 'string') return result;
          }
          return moment.utc(iso).local().format(format.replace(' z', ''));
        };

        const buildDurationDisplay = (duration: string, escalatedAt: string, deEscalatedAt: string) => {
          const start = formatDate(escalatedAt, 'MMM D [at] h:mm A');
          const end = formatDate(deEscalatedAt, 'MMM D [at] h:mm A');
          const tzAbbr = timezone
            ? (convertUtcToRelevantTimezone<string>(deEscalatedAt, timezone, 'z') || '')
            : '';
          const range = tzAbbr ? `(${start} - ${end}) ${tzAbbr}` : `(${start} - ${end})`;
          return `${duration} ${range}`.trim();
        };

        if (modalProps.type === 'de-escalation') {
          const rawDuration = responseData?.escalation_duration || '';
          const escalatedAt = responseData?.escalated_at || '';
          const deEscalatedAt = responseData?.de_escalated_at || '';
          setData({
            address: responseData?.property_info?.address || '',
            reason: responseData?.reason || '',
            notes: responseData?.notes || '',
            deEscalatedBy: responseData?.de_escalated_by || '',
            deEscalatedAt: deEscalatedAt ? formatDate(deEscalatedAt) : '',
            escalatedAt: escalatedAt ? formatDate(escalatedAt) : '',
            escalationDuration: (rawDuration && escalatedAt && deEscalatedAt)
              ? buildDurationDisplay(rawDuration, escalatedAt, deEscalatedAt)
              : rawDuration,
          });
        } else {
          setData({
            address: responseData?.property_info?.address || '',
            reason: responseData?.reason || '',
            notes: responseData?.notes || '',
            flaggedBy: responseData?.flagged_by || '',
            flaggedAt: responseData?.flagged_at ? formatDate(responseData.flagged_at) : '',
          });
        }
      })
      .catch((error) => {
        handleErrorMessages({
          errors: error?.message || error?.errors,
        }, (msg: string) => toast(msg, { type: 'error' }));
            dispatch(updateVisibility({ modalType: 'escalationStatusModal', isVisible: false }));
      })
      .finally(() => setIsLoading(false));
  }, [modalProps?.escalationId, modalProps?.type, modalProps?.timezone, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(updateVisibility({ modalType: 'escalationStatusModal', isVisible: false }));
  }, [dispatch]);

  const {
    address, reason, notes,
    flaggedBy, flaggedAt,
    deEscalatedBy, deEscalatedAt, escalationDuration,
  } = data;

  const title = isDeEscalation ? 'Escalation Flag Removed Details' : 'Escalation Details';
  const propertyAddress = address || 'this property';

  const introText = isDeEscalation
    ? `The property at ${propertyAddress} has been De-escalated.`
    : `The property at ${propertyAddress} has been flagged as escalated. Please review the details below and take action as needed.`;

  const footerText = isDeEscalation
    ? deEscalatedBy && deEscalatedAt
      ? `Escalation flag removed on ${deEscalatedAt}.`
      : null
    : flaggedBy && flaggedAt
      ? `Flagged on ${flaggedAt}.`
      : null;

  const reasonLabel = isDeEscalation ? 'Escalation flag removed: ' : 'Escalation Reason: ';

  return (
    <Modal
      isOpen
      toggle={handleClose}
      className="escalation-status-modal"
      centered
    >
      <div className="modal-header">
        <h5 className="modal-title">{title}</h5>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        />
      </div>
      <ModalBody>
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center mh-200">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="esm-text-block">
            <p>{introText}</p>
            {reason && (
              <>
                <p>&zwj;</p>
                <p className="esm-bold">{reasonLabel}</p>
                <p>{reason}</p>
              </>
            )}
            {notes && (
              <>
                <p>&zwj;</p>
                <p className="esm-bold">{'Notes: '}</p>
                <p>{notes}</p>
              </>
            )}
            {isDeEscalation && escalationDuration && (
              <>
                <p>&zwj;</p>
                <p className="esm-bold">{'Escalation Duration: '}</p>
                <p>{escalationDuration}</p>
              </>
            )}
            {footerText && (
              <>
                <p>&zwj;</p>
                <p>{footerText}</p>
              </>
            )}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default EscalationStatusModal;
